/**
 * Connect-flow service — Workstream A2
 *
 * Thin apiClient wrappers for the GSC/GA4 connect wizard. All functions throw
 * on HTTP errors (never swallow to undefined). Discovery endpoints may return
 * a 200 with an `error` field when OAuth is not configured — callers handle
 * that as a distinct state from a network/HTTP error.
 *
 * Backend A1 (commit b7488e5a8): discovery endpoints are tenant-filtered and
 * return verbatim property strings. Tenant-settings PATCH accepts the three
 * connect fields. GSC requires a 90-day backfill after connect.
 */

import { apiClient } from './apiClient';

// ---------------------------------------------------------------------------
// Grant-access destinations (doc 61 §C2 (c)1)
// ---------------------------------------------------------------------------
// Under auth model (a) the operator must grant the agency Google account access
// in Google's own admin BEFORE the picker can see the property. These are the
// feedforward link targets on the intro + permission_denied states.

/** Google Search Console admin — grant the agency account access here. */
export const GSC_ADMIN_URL = 'https://search.google.com/search-console';
/** Google Analytics 4 admin — grant the agency account access here. */
export const GA4_ADMIN_URL = 'https://analytics.google.com';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GscSite {
	/** Verbatim GSC property string — sc-domain:example.com or https://example.com/ */
	site_url: string;
	permission_level: string;
}

export interface Ga4Property {
	account_name: string;
	/** Numeric GA4 property ID, e.g. "123456789" */
	property_id: string;
	property_name: string;
}

/** Discovery response — `error` present when OAuth credentials are missing. */
export interface GscSitesResponse {
	sites: GscSite[];
	error?: string;
}

export interface Ga4PropertiesResponse {
	properties: Ga4Property[];
	error?: string;
}

export interface GscConnectionFields {
	gsc_site_url: string;
	gsc_enabled: boolean;
}

export interface Ga4ConnectionFields {
	ga4_property_id: string;
}

export interface SyncResult {
	status: string;
	rows_synced?: number;
	error?: string;
}

/**
 * GSC sync-status — the activation-spectrum source (doc 30 §4 / doc 61 G6).
 * `GET /search-performance/sync-status` returns the latest sync log, or
 * `{ status: "never_synced" }` (a valid 200, NOT an error) when no sync exists;
 * every other field is absent in that case, so they are optional. HTTP errors
 * throw — callers degrade to the pre-C1 fallback body, never a false "failed".
 */
export interface GscSyncStatus {
	/** running | completed | failed | never_synced */
	status: string;
	sync_started_at?: string | null;
	sync_completed_at?: string | null;
	rows_synced?: number | null;
	error_message?: string | null;
	date_range_start?: string | null;
	date_range_end?: string | null;
}

/** Connect-relevant fields from tenant settings (GET /tenant-settings/{website}). */
export interface TenantConnectSettings {
	gsc_site_url: string | null;
	gsc_enabled: boolean | null;
	ga4_property_id: string | null;
}

// ---------------------------------------------------------------------------
// Tenant settings read (already-connected guard)
// ---------------------------------------------------------------------------

export async function fetchTenantConnectSettings(
	tenantId: string,
): Promise<TenantConnectSettings> {
	const { data } = await apiClient.get<TenantConnectSettings>(
		`/tenant-settings/${tenantId}`,
	);
	return data;
}

// ---------------------------------------------------------------------------
// Discovery (tenant-filtered, verbatim property strings)
// ---------------------------------------------------------------------------

export async function fetchGscSites(
	tenantId: string,
): Promise<GscSitesResponse> {
	const { data } = await apiClient.get<GscSitesResponse>(
		'/search-performance/sites',
		{ params: { tenant_id: tenantId } },
	);
	return data;
}

export async function fetchGa4Properties(
	tenantId: string,
): Promise<Ga4PropertiesResponse> {
	const { data } = await apiClient.get<Ga4PropertiesResponse>(
		'/ga4/properties',
		{ params: { tenant_id: tenantId } },
	);
	return data;
}

// ---------------------------------------------------------------------------
// Connection (tenant-settings PATCH — partial update, Redis cache invalidated
// server-side by the route's CacheDep)
// ---------------------------------------------------------------------------

export async function saveGscConnection(
	tenantId: string,
	fields: GscConnectionFields,
): Promise<void> {
	await apiClient.patch(`/tenant-settings/${tenantId}`, fields);
}

export async function saveGa4Connection(
	tenantId: string,
	fields: Ga4ConnectionFields,
): Promise<void> {
	await apiClient.patch(`/tenant-settings/${tenantId}`, fields);
}

// ---------------------------------------------------------------------------
// Backfill (GSC only — 90-day cap; GA4 is instant)
// ---------------------------------------------------------------------------

export async function triggerGscBackfill(
	tenantId: string,
): Promise<SyncResult> {
	const { data } = await apiClient.post<SyncResult>(
		'/search-performance/sync',
		null,
		{
			params: {
				tenant_id: tenantId,
				days_back: 90,
			},
		},
	);
	return data;
}

// ---------------------------------------------------------------------------
// Sync-status (activation spectrum: running / completed / failed / never_synced)
// ---------------------------------------------------------------------------

export async function fetchGscSyncStatus(
	tenantId: string,
): Promise<GscSyncStatus> {
	const { data } = await apiClient.get<GscSyncStatus>(
		'/search-performance/sync-status',
		{ params: { tenant_id: tenantId } },
	);
	return data;
}
